import React, { useContext } from "react";
import { CartContext } from "../Component/CartContext";
import productData from "../Datas/productData";
import "../css/cartpage.scss";
import { Link, useNavigate } from "react-router-dom";
import Footer from "./Footer";
import Header from "./Header";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);

  const getProductById = (id) => {
    return productData.find((product) => product.id === id);
  };

  const formatMoney = (value) => {
    const n = Number(value);
    return `$${(Number.isFinite(n) ? n : 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const calculateOriginalSubtotal = () => {
    let total = 0;
    cartItems.forEach((item) => {
      const product = getProductById(item.id);
      if (!product) return;
      const price = Number(product.price);
      total += (Number.isFinite(price) ? price : 0) * item.quantity;
    });
    return total;
  };

  const calculateSaleSubtotal = () => {
    let total = 0;
    cartItems.forEach((item) => {
      const product = getProductById(item.id);
      if (!product) return;
      const price = Number(product.price);
      const original = Number.isFinite(price) ? price : 0;
      total += original * 0.75 * item.quantity;
    });
    return total;
  };

  const subtotal = calculateOriginalSubtotal();
  const saleSubtotal = calculateSaleSubtotal();
  const shipping = cartItems.length ? 25 : 0;
  const totalSavings = subtotal - saleSubtotal;

  const handleUpdateQuantity = (id, size, color, newQuantity) => {
    if (newQuantity > 0) {
      updateQuantity(id, size, color, newQuantity);
    }
  };

  const handleCheckout = () => {
    if (cartItems.length > 0) {
      navigate("/checkout");
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      alert("Your cart is empty");
    }
  };

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <Header />

        <main className="cart">
          <div className="cart__inner cart__empty">
            <h1>Your Cart</h1>
            <p>Not quite ready to check out?</p>
            <Link to="/shop" onClick={toTop} className="cart__continue">
              Continue Shopping
            </Link>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="cart-page">
      <Header />

      <main className="cart">
        <div className="cart__inner">
          <section className="cart__left">
            <div className="cart__header">
              <h1>Your Cart</h1>
              <div className="cart__subhead">
                Not quite ready to check out?{" "}
                <Link to="/shop" onClick={toTop}>
                  Continue Shopping.
                </Link>
              </div>
            </div>

            <div className="cart__items">
              {cartItems.map((item) => {
                const product = getProductById(item.id);
                if (!product) return null;

                const rawPrice = Number(product.price);
                const originalPrice = Number.isFinite(rawPrice) ? rawPrice : 0;
                const salePrice = originalPrice * 0.75;
                const lineOriginal = originalPrice * item.quantity;
                const lineSale = salePrice * item.quantity;
                const isNew = product.id % 3 === 0;

                return (
                  <div
                    className="cart-item"
                    key={`${item.id}-${item.size}-${item.color}`}
                  >
                    <div className="cart-item__media">
                      <img src={product.image} alt={product.name} />
                      <button type="button" className="cart-item__edit">
                        Edit
                      </button>
                    </div>

                    <div className="cart-item__content">
                      {isNew && (
                        <div className="cart-item__badge">NEW ARRIVAL</div>
                      )}
                      <div className="cart-item__brand">
                        {product.brand || "Denowl"}
                      </div>
                      <div className="cart-item__name">{product.name}</div>

                      <div className="cart-item__details">
                        <div>Color: {item.color || product.colors?.[0]}</div>
                        <div>Size: {item.size || product.size?.[0]}</div>
                        <div>Quantity: {item.quantity}</div>
                        <div className="cart-item__eta">
                          Estimated Arrival: April 07 - April 07
                        </div>
                      </div>

                      <div className="cart-item__actions">
                        <button type="button" className="cart-action">
                          Add to Favorites
                        </button>
                        <button
                          type="button"
                          className="cart-action"
                          onClick={() =>
                            removeFromCart(item.id, item.size, item.color)
                          }
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <div className="cart-item__right">
                      <div className="cart-item__qty">
                        <button
                          type="button"
                          aria-label="Decrease quantity"
                          onClick={() =>
                            handleUpdateQuantity(
                              item.id,
                              item.size,
                              item.color,
                              item.quantity - 1
                            )
                          }
                        >
                          −
                        </button>
                        <div className="cart-item__qtyValue">
                          {item.quantity}
                        </div>
                        <button
                          type="button"
                          aria-label="Increase quantity"
                          onClick={() =>
                            handleUpdateQuantity(
                              item.id,
                              item.size,
                              item.color,
                              item.quantity + 1
                            )
                          }
                        >
                          +
                        </button>
                      </div>

                      <div className="cart-item__prices">
                        <div className="cart-item__priceOriginal">
                          {formatMoney(lineOriginal)}
                        </div>
                        <div className="cart-item__priceSale">
                          {formatMoney(lineSale)}
                        </div>
                        <div className="cart-item__discount">25% off</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <aside className="cart__right">
            <div className="summary">
              <h2>Order Summary</h2>

              <div className="summary-row">
                <span>SUBTOTAL</span>
                <span>{formatMoney(subtotal)}</span>
              </div>
              <div className="summary-row">
                <span>SHIPPING</span>
                <span>{formatMoney(shipping)}</span>
              </div>
              <div className="summary-row summary-savings">
                <span>TOTAL SAVINGS</span>
                <span>-{formatMoney(totalSavings)}</span>
              </div>

              <div className="summary-total">
                <span>TOTAL</span>
                <span>TBD</span>
              </div>

              <button className="summary-checkout" onClick={handleCheckout}>
                Checkout
              </button>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CartPage;
